/**
 * Extrae folio / clave / últimos 4 cuenta destino desde OCR de captura.
 * Solo autocompleta campos vacíos (ver aplicarAutocompletadoCamposCaptura).
 *
 * Ejemplos soportados:
 * - BBVA: Folio de (la) operación + Cuenta de destino •5957
 * - Banorte SPEI: Clave de rastreo + CLABE ****9571
 * - Banregio: folio 12 dígitos / R3D…
 * - Mercado Pago: Comprobante #180512215736 + destino ****0232 (no origen ****0523)
 * - Banxico CEP: Beneficiario CLABE + Clave de rastreo
 * - TPV: AUT # → clave, # DE REF → folio
 *
 * Fallback: sin clave → folio; sin folio → clave.
 */

function textoCampoVacio(v) {
  return v == null || String(v).trim() === ''
}

function soloDigitos(v) {
  return String(v == null ? '' : v).replace(/\D/g, '')
}

function padFolioBbva(digitos) {
  var d = soloDigitos(digitos)
  if (d.length === 10) return d
  if (d.length >= 7 && d.length <= 12) return d.padStart(10, '0')
  return d || null
}

function ultimos4DeDigitos(token) {
  var d = soloDigitos(token)
  if (d.length >= 4) return d.slice(-4)
  return null
}

function normalizarOcr(texto) {
  return String(texto || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

function lineasDePalabras(palabras) {
  if (!Array.isArray(palabras) || !palabras.length) return []
  return palabras
    .map(function (p) {
      return String((p && (p.LineText || p.text || p)) || '').trim()
    })
    .filter(Boolean)
}

function valorTrasEtiquetaEnLineas(lines, predEtiqueta, predValor) {
  for (var i = 0; i < lines.length; i++) {
    if (!predEtiqueta(lines[i])) continue
    if (predValor(lines[i])) return predValor(lines[i])
    for (var j = 1; j <= 3; j++) {
      var next = lines[i + j] || ''
      var v = predValor(next)
      if (v) return v
    }
  }
  return null
}

/**
 * Recorta el OCR a la sección de destino/beneficiario (evita CLABE de origen/ordenante).
 */
function textoSeccionDestino(raw) {
  var s = String(raw || '')
  var n = normalizarOcr(s)

  // Encabezado "Beneficiario" (no "cuenta beneficiaria")
  var idxBenef = -1
  var reBenefHead = /(?:^|\n)\s*beneficiario\s*(?:\n|:|$)/g
  var bm
  while ((bm = reBenefHead.exec(n))) {
    idxBenef = bm.index + (bm[0].indexOf('beneficiario'))
  }
  if (idxBenef < 0) {
    // Fallback: última palabra beneficiario suelta
    var reB = /\bbeneficiario\b/g
    while ((bm = reB.exec(n))) idxBenef = bm.index
  }
  if (idxBenef >= 0) return s.slice(idxBenef)

  var idxReceptora = n.search(/institucion\s+receptora/)
  if (idxReceptora >= 0) return s.slice(idxReceptora)

  // Mercado Pago / apps: bloque "Destino" (no "Origen y destino" solo)
  var idxDestinoSolo = n.search(/(?:^|\n)\s*destino\b/)
  if (idxDestinoSolo >= 0) return s.slice(idxDestinoSolo)

  var idxCuentaDest = n.search(/cuenta\s+(?:de\s+)?destino/)
  if (idxCuentaDest >= 0) return s.slice(idxCuentaDest)

  // "Origen y destino" → tomar desde la 2.ª mitad (después del primer CLABE/origen)
  var idxOyD = n.search(/origen\s+y\s+destino/)
  if (idxOyD >= 0) {
    var desde = s.slice(idxOyD)
    var nDesde = normalizarOcr(desde)
    // Segunda aparición de clabe / después de banregio|bbva|escuela en destino
    var mBan = nDesde.search(/\b(banregio|bbva|bancomer|escuela)\b/)
    if (mBan >= 0) return desde.slice(mBan)
    var firstClabe = nDesde.search(/clabe/)
    if (firstClabe >= 0) {
      var secondClabe = nDesde.indexOf('clabe', firstClabe + 5)
      if (secondClabe >= 0) return desde.slice(secondClabe)
    }
  }

  // Quitar bloque origen/ordenante si aparece primero
  var sinOrigen = s.replace(
    /(?:cuenta\s+(?:de\s+)?origen|ordenante)[\s\S]{0,220}/gi,
    ' '
  )
  return sinOrigen
}

/** Folio de operación / referencia / comprobante / R3D / REF TPV. */
export function extraerFolioOperacionOcr(texto, palabras) {
  var raw = String(texto || '')
  var t = normalizarOcr(raw)
  var lines = lineasDePalabras(palabras)

  // BBVA: Folio de (la) operación
  var mOp = t.match(/folio(?:\s+de)?(?:\s+la)?\s+operacion\s*[:\-]?\s*(\d{7,12})\b/)
  if (mOp) return padFolioBbva(mOp[1])

  var mInt = t.match(/folio(?:\s+de)?\s+internet\s*[:\-]?\s*(\d{10})\b/)
  if (mInt) return padFolioBbva(mInt[1])

  // Ecommerce Banregio
  var mR3d = raw.match(/\bR3D[A-Za-z0-9]{4,40}\b/i)
  if (mR3d) return mR3d[0]

  var mFolioLabel = raw.match(/\bFolio\s*[:\-]?\s*([A-Za-z0-9][A-Za-z0-9_-]{4,40})\b/i)
  if (mFolioLabel) {
    var cand = mFolioLabel[1]
    if (!/^(de|la|operacion|internet)$/i.test(cand)) {
      if (/^\d{7,12}$/.test(cand)) return padFolioBbva(cand)
      if (/^R3D/i.test(cand) || /[A-Za-z]/.test(cand)) return cand
    }
  }

  // Mercado Pago: Comprobante #180512215736
  var mComp = raw.match(/\bComprobante\s*[:\-]?\s*#?\s*(\d{8,20})\b/i)
  if (mComp) return mComp[1]
  var mHash = raw.match(/#\s*(\d{10,20})\b/)
  if (mHash && /comprobante|mercado\s*pago|wallet/i.test(raw)) return mHash[1]

  // TPV: # DE REF / REF
  var mRefTpv = raw.match(/#\s*DE\s*REF\s*[:\-]?\s*(\d{6,20})\b/i)
  if (mRefTpv) return mRefTpv[1]

  // Banregio app: folio numérico 10–14 bajo el monto (sin etiqueta)
  if (/banregio|transferencia exitosa/i.test(raw)) {
    var mBan = raw.match(/\b(\d{10,14})\b/)
    if (mBan && !/clave|rastreo/i.test(raw.slice(Math.max(0, raw.indexOf(mBan[1]) - 30), raw.indexOf(mBan[1])))) {
      return mBan[1]
    }
  }

  // Líneas OCR
  var fromLines = valorTrasEtiquetaEnLineas(
    lines,
    function (ln) {
      var n = normalizarOcr(ln)
      return (
        /folio/.test(n) ||
        /comprobante\s*#?/.test(n) ||
        /#\s*de\s*ref/.test(n)
      )
    },
    function (ln) {
      var r3 = ln.match(/\bR3D[A-Za-z0-9]{4,40}\b/i)
      if (r3) return r3[0]
      var hash = ln.match(/#\s*(\d{8,20})\b/)
      if (hash) return hash[1]
      var dig = ln.match(/(\d{7,14})\b/)
      if (dig) return dig[1].length === 10 || dig[1].length === 12 ? padFolioBbva(dig[1]) || dig[1] : dig[1]
      return null
    }
  )
  if (fromLines) return fromLines

  return null
}

/**
 * Aut. Clave Rastreo: SPEI / AUT TPV.
 */
export function extraerClaveRastreoOcr(texto, palabras) {
  var raw = String(texto || '')
  var up = raw.toUpperCase()
  var lines = lineasDePalabras(palabras)

  var mLabel = up.match(/CLAVE\s+(?:DE\s+)?RASTREO\s*[:\-]?\s*([A-Z0-9]{8,40})\b/)
  if (mLabel) return mLabel[1]

  var mHsbc = up.match(/\bHSBC\d{6,}\b/)
  if (mHsbc) return mHsbc[0]

  // TPV Global Payments: AUT # 920837
  var mAut = up.match(/\bAUT(?:ORIZACION)?\s*#?\s*[:\-]?\s*([A-Z0-9]{5,20})\b/)
  if (mAut && !/CLAVE|RASTREO/.test(mAut[0])) return mAut[1]

  var fromLines = valorTrasEtiquetaEnLineas(
    lines,
    function (ln) {
      var L = ln.toUpperCase()
      return (/CLAVE/.test(L) && /RASTREO/.test(L)) || /\bAUT\s*#/.test(L) || /^AUT\b/.test(L)
    },
    function (ln) {
      var L = ln.toUpperCase().replace(/[^A-Z0-9#]/g, ' ')
      var m = L.match(/([A-Z0-9]{8,40})/)
      if (m && /[A-Z]/.test(m[1]) && /\d/.test(m[1])) return m[1]
      var m2 = L.match(/\b(\d{5,12})\b/)
      if (m2 && /AUT/i.test(ln)) return m2[1]
      return null
    }
  )
  if (fromLines) return fromLines

  var partes = up.split(/[^A-Z0-9]+/)
  for (var pi = 0; pi < partes.length; pi++) {
    var p = partes[pi]
    if (p.length < 12 || p.length > 40) continue
    if (!/[A-Z]/.test(p) || !/\d/.test(p)) continue
    if (/^R3D/i.test(p)) continue
    return p
  }

  return null
}

/**
 * Números tipo CLABE/cuenta en un texto (16–18 dígitos; OCR a veces pierde 1).
 */
function numerosCuentaLargos(texto) {
  var s = String(texto || '')
  var out = []
  var re = /\b(\d{16,18})\b/g
  var m
  while ((m = re.exec(s))) out.push(m[1])
  // "CLABE, Tarjeta…: 0125…" (coma/texto entre etiqueta y dígitos)
  var reClabe = /clabe[^\d\n]{0,120}(\d{10,18})\b/gi
  while ((m = reClabe.exec(s))) {
    if (out.indexOf(m[1]) < 0) out.push(m[1])
  }
  return out
}

/**
 * Últimos 4 de cuenta/CLABE de DESTINO (no origen / ordenante).
 */
export function extraerCuentaDestinoUltimos4Ocr(texto, palabras) {
  var raw = String(texto || '')
  var lines = lineasDePalabras(palabras)
  var seccion = textoSeccionDestino(raw)

  // 1) CLABE/cuenta larga en sección destino/beneficiario (Banxico / SPEI)
  var largos = numerosCuentaLargos(seccion)
  if (largos.length) {
    return ultimos4DeDigitos(largos[largos.length - 1])
  }

  // 2) Máscara ****0232 / •5957 junto a CLABE / destino (Mercado Pago, BBVA)
  var patterns = [
    /cuenta\s+(?:de\s+)?destino\s*[:\-]?\s*(?:clabe\s*)?[*\u2022Xx×·•●∙Oo0\s]*(\d{4,20})\b/i,
    /(?:clabe|cuenta)\s+(?:de\s+)?destino\s*[:\-]?\s*[*\u2022Xx×·•●∙Oo0\s]*(\d{4,20})\b/i,
    /clabe\s*[*\u2022Xx×·•●∙Oo0\s]*(\d{4})\b/i,
    /[*\u2022Xx×·•●∙]{2,}\s*(\d{4})\b/,
  ]
  for (var i = 0; i < patterns.length; i++) {
    var m = seccion.match(patterns[i])
    if (m) return ultimos4DeDigitos(m[1])
  }

  // 3) Tras BANREGIO / BBVA en sección destino (Mercado Pago: banco luego CLABE ****)
  var mBancoClabe = seccion.match(
    /(?:banregio|bbva|bancomer|hsbc|banorte)[\s\S]{0,200}clabe[^\d\n]{0,80}(\d{4,20})\b/i
  )
  if (mBancoClabe) return ultimos4DeDigitos(mBancoClabe[1])

  // 4) Todas las máscaras ****dddd en sección → última (Mercado: origen luego destino)
  var mascaras = seccion.match(/[*\u2022Xx×·•●∙]{2,}\s*(\d{4})\b/g)
  if (mascaras && mascaras.length) {
    var lastMask = mascaras[mascaras.length - 1].match(/(\d{4})\b/)
    if (lastMask) return lastMask[1]
  }

  // 5) Ventana tras etiqueta destino / clabe (buscar todas; preferir la de clabe con dígitos)
  var reVentana = /(?:cuenta\s+(?:de\s+)?destino|destino|clabe)\s*[:\-]?\s*([^\n\r]{0,160})/gi
  var vm
  var ultimoPick = null
  while ((vm = reVentana.exec(seccion))) {
    if (!vm[1] || /origen|ordenante/i.test(vm[1])) continue
    var bloques = vm[1].match(/\d{4,20}/g)
    if (!bloques || !bloques.length) continue
    var preferLargos = bloques.filter(function (d) { return d.length >= 10 })
    ultimoPick = preferLargos.length
      ? preferLargos[preferLargos.length - 1]
      : bloques[bloques.length - 1]
  }
  if (ultimoPick) return ultimos4DeDigitos(ultimoPick)

  // 6) BBVA layout: "Destino Escuela … •5604"
  var mBulletDest = seccion.match(
    /destino[\s\S]{0,80}[*\u2022Xx×·•●∙]\s*(\d{4})\b/i
  )
  if (mBulletDest) return mBulletDest[1]

  // 7) Líneas OCR (preferir beneficiario / destino)
  var enBeneficiario = false
  for (var li = 0; li < lines.length; li++) {
    var line = lines[li]
    var n = normalizarOcr(line)
    if (/\bbeneficiario\b/.test(n) || /institucion\s+receptora/.test(n)) enBeneficiario = true
    if (/\bordenante\b/.test(n) || (/origen/.test(n) && !/destino/.test(n))) {
      enBeneficiario = false
      continue
    }
    if (/origen/.test(n) && !/destino/.test(n) && !enBeneficiario) continue

    var esDestino =
      enBeneficiario ||
      /cuenta\s+(?:de\s+)?destino/.test(n) ||
      (/destino/.test(n) && (/cuenta|clabe|escuela|banregio|bbva|\d/.test(n) || /destino\s*$/.test(n))) ||
      (/^clabe\b/.test(n) && (enBeneficiario || (li > 0 && /destino|beneficiario|banregio|bbva|escuela/.test(normalizarOcr(lines[li - 1] || '')))))

    if (!esDestino && !(/^destino\b/.test(n))) continue

    var fromSame = line.match(/\d{4,20}/g)
    if (fromSame && fromSame.length) {
      var prefer = fromSame.filter(function (d) { return d.length >= 7 })
      var pickLine = prefer.length ? prefer[prefer.length - 1] : fromSame[fromSame.length - 1]
      return ultimos4DeDigitos(pickLine)
    }

    for (var j = 1; j <= 5; j++) {
      var next = lines[li + j] || ''
      if (/origen|ordenante/i.test(next) && !/destino|beneficiario/i.test(next)) continue
      var digs = next.match(/\d{4,20}/g)
      if (!digs) continue
      if (
        /^[*\u2022Xx×·•●∙Oo0\s]*\d{4,20}\s*$/i.test(next) ||
        /cuenta|clabe|\*|•|banregio|bbva/i.test(next) ||
        digs.some(function (d) { return d.length >= 7 })
      ) {
        var preferNext = digs.filter(function (d) { return d.length >= 7 })
        var pickNext = preferNext.length
          ? preferNext[preferNext.length - 1]
          : digs[digs.length - 1]
        return ultimos4DeDigitos(pickNext)
      }
    }
  }

  // 8) Último recurso: última cuenta larga SOLO en sección destino (nunca ordenante suelto)
  var largosRawSec = numerosCuentaLargos(seccion)
  if (largosRawSec.length) return ultimos4DeDigitos(largosRawSec[largosRawSec.length - 1])

  return null
}

export function extraerCamposCapturaComprobante(texto, palabras) {
  var folio = extraerFolioOperacionOcr(texto, palabras)
  var clave = extraerClaveRastreoOcr(texto, palabras)
  var cuenta = extraerCuentaDestinoUltimos4Ocr(texto, palabras)

  // Si falta uno, copiar el valor del otro
  if (!clave && folio) clave = folio
  if (!folio && clave) folio = clave

  return {
    folio_operacion: folio,
    aut_clave_rastreo: clave,
    cuenta: cuenta,
  }
}

export function aplicarAutocompletadoCamposCaptura(ctx, texto, palabras) {
  if (!ctx || !texto) return
  var campos = extraerCamposCapturaComprobante(texto, palabras)
  if (campos.folio_operacion && textoCampoVacio(ctx.folio_operacion)) {
    ctx.folio_operacion = campos.folio_operacion
  }
  if (campos.aut_clave_rastreo && textoCampoVacio(ctx.aut_clave_rastreo)) {
    ctx.aut_clave_rastreo = campos.aut_clave_rastreo
  }
  if (campos.cuenta && textoCampoVacio(ctx.cuenta)) {
    ctx.cuenta = campos.cuenta
  }
}

export { textoCampoVacio }
